import { inferProductionCapitalPackage } from './productionCapitalCampaign.js';

/**
 * shareRowDisplay — display-only helper for per-row share counts.
 *
 * PROBLEM this solves:
 *   Birthday/gift rows in `aureus_share_purchases` are stored with
 *   shares_purchased = 0 and the granted amount in `bonus_shares`
 *   (e.g. payment_method 'BIRTHDAY_2026_BUYER_GIFT' / '..._UPLINE_GIFT',
 *   package_name 'Phase 9 Birthday Gift'). Any table that renders
 *   `shares_purchased` alone therefore shows 0 for a gift row even though
 *   the shares exist in `bonus_shares` and are already counted in the
 *   user's total holdings.
 *
 * SCOPE / SAFETY:
 *   This is PRESENTATIONAL ONLY. It never mutates data, never changes any
 *   stored share/bonus amount, and never inflates supply. It returns the
 *   *effective* share count for a single row so the line-item matches the
 *   value that already exists in the database:
 *     - normal purchase row  -> shares_purchased (unchanged)
 *     - pure gift/bonus row  -> bonus_shares (the real granted amount)
 *     - artifact 0/0 row     -> 0 (value lives elsewhere; not fabricated)
 */

export interface ShareRowLike {
  shares_purchased?: number | string | null;
  bonus_shares?: number | string | null;
  payment_method?: string | null;
  package_name?: string | null;
  transaction_reference?: string | null;
}

const toNum = (v: number | string | null | undefined): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0));
  return Number.isFinite(n) ? n : 0;
};

/**
 * Effective per-row share count for display.
 * Returns shares_purchased whenever it is non-zero (INCLUDING negatives, so
 * auditable VOID/reversal rows subtract correctly and per-row totals match the
 * canonical user_share_balances.net_shares); otherwise falls back to
 * bonus_shares (which is 0 for genuine 0/0 artifact rows).
 *
 * Verified (read-only, live prod fgubaqoftdeefcakejwu): across the 408 users
 * holding an active negative/VOID gift row, this signed fold matches canonical
 * net_shares for 377 (vs 6 under the old `> 0` fold) and cuts the client-side
 * over-count from +4,426 shares to ~-120. The residual ~31 users are a
 * PRE-EXISTING view-vs-rows divergence in net_shares (unchanged by this helper).
 */
export const effectiveRowShares = (row: ShareRowLike): number => {
  const purchased = toNum(row?.shares_purchased);
  if (purchased !== 0) return purchased;
  return toNum(row?.bonus_shares);
};

/**
 * True when the row's displayed shares come from bonus_shares (a gift/bonus
 * row with shares_purchased = 0 and bonus_shares > 0). Useful for adding a
 * small "gift" qualifier next to the number without changing the value.
 */
export const isBonusOnlyRow = (row: ShareRowLike): boolean =>
  toNum(row?.shares_purchased) === 0 && toNum(row?.bonus_shares) > 0;

// v4.8.250: kept in step with BONUS_AS_SHARES_METHODS in lib/canonicalBalances.js and with
// the admin Master Audit bonus column (supabase/migrations/20260916_bonus_column_net_correct.sql),
// so a share is classified the same way on the member dashboard and in the admin view.
// `Legacy Bonus` and `Glitch Correction` were previously missing here, so those free shares —
// stored in shares_purchased with total_amount 0 — rendered under "Paid Shares". An account
// holding 301 Legacy Bonus shares therefore showed "+3.00 bonus" while 301 free shares sat on
// the paid line. `_GIFT$` already covers the PCAP / BIRTHDAY / SINGLE_SPECIAL buyer and upline
// gifts. SINGLE_SPECIAL_2026_CORRECTION is intentionally NOT listed: it carries negative shares
// and is caught by isReversalRow, which keeps it on its own auditable negative line.
const GIFT_METHOD_RE = /(_GIFT$)|(^Consolidated Bonus$)|(^Legacy Bonus$)|(^Glitch Correction$)/i;
const GIFT_PACKAGE_RE = /(Special Gift)|(Birthday Gift)|(Anniversary Gift)|(Consolidated bonus)|(Legacy bonus)|(BOGO)|(Single[- ]Share Special)/i;

/**
 * Canonical display classifier: true when a row represents a gift/bonus grant
 * of ANY stored shape — the newer `bonus_shares > 0` shape, the
 * "Consolidated Bonus" shape (amount in shares_purchased), or the VOID/reversal
 * correction rows. Keys off the payment_method / package_name markers rather
 * than which numeric column happens to be populated, so all three shapes are
 * detected. PRESENTATIONAL ONLY — never mutates data or amounts.
 */
export const isGiftRow = (row: ShareRowLike): boolean => {
  const method = String(row?.payment_method ?? '');
  const pkg = String(row?.package_name ?? '');
  if (GIFT_METHOD_RE.test(method)) return true;
  if (GIFT_PACKAGE_RE.test(pkg)) return true;
  return isBonusOnlyRow(row);
};

/**
 * True when a row is an auditable reversal/void of a prior grant: its
 * transaction_reference is a `VOID:` marker, or its shares_purchased is
 * negative (an offsetting clawback). These must be shown as a distinct
 * negative line, never hidden or "corrected away". PRESENTATIONAL ONLY.
 */
export const isReversalRow = (row: ShareRowLike): boolean => {
  const ref = String(row?.transaction_reference ?? '');
  if (/^VOID:/i.test(ref)) return true;
  return toNum(row?.shares_purchased) < 0;
};

/**
 * True when a row is an append-only RECREDIT correction (e.g. restoring equity
 * after a VOID×freeze over-deduction). These reuse a *_GIFT payment_method for
 * trigger safety but are NOT bonus grants — they restore paid/legit shares.
 * PRESENTATIONAL ONLY.
 */
export const isRecreditRow = (row: ShareRowLike): boolean =>
  /^RECREDIT:/i.test(String(row?.transaction_reference ?? ''));

/**
 * True when a row should appear on the user-facing Bonus Shares line/section:
 * any gift/bonus shape (live bonus_shares OR Consolidated Bonus), excluding
 * VOID reversals and RECREDIT restorations. PRESENTATIONAL ONLY.
 */
export const isActiveBonusRow = (row: ShareRowLike): boolean =>
  isGiftRow(row) && !isReversalRow(row) && !isRecreditRow(row);

/**
 * Promo extra shares stored inside shares_purchased (paid < shares * list price).
 * Display only. Returns 0 when bonus_shares is already populated or the row is a gift/reversal.
 */
export const impliedPromoBonusShares = (row: ShareRowLike & {
  total_amount?: number | string | null;
  price_per_share?: number | string | null;
}): number => {
  if (isGiftRow(row) || isReversalRow(row) || isRecreditRow(row)) return 0;
  if (toNum(row?.bonus_shares) > 0) return 0;
  const shares = toNum(row?.shares_purchased);
  const price = toNum(row?.price_per_share);
  const paid = toNum(row?.total_amount);
  if (shares <= 0 || price <= 0 || paid <= 0) return 0;
  // Official P1–P5 qty=1 lots already carry their bonus on a gift row.
  // Phase-list $200 vs $100 paid is a discount, not extra shares.
  const official = inferProductionCapitalPackage({ paidShares: shares, amountUsd: paid });
  if (official && official.qty === 1) return 0;
  const implied = shares - paid / price;
  if (!Number.isFinite(implied) || implied < 0.009) return 0;
  return Math.round(implied * 100) / 100;
};

export const displayRowBonusShares = (row: ShareRowLike & {
  total_amount?: number | string | null;
  price_per_share?: number | string | null;
}): number => {
  const stored = toNum(row?.bonus_shares);
  if (stored > 0) return stored;
  return impliedPromoBonusShares(row);
};

/**
 * Display-only re-label of the word "Birthday" -> "Anniversary" in a
 * user-facing package/label string.
 *
 * SCOPE / SAFETY:
 *   Presentational ONLY. The stored package_name / payment_method values in the
 *   database (e.g. 'Phase 9 Birthday Gift', 'Presale Birthday Gift') are NEVER
 *   changed, and no code that matches on those raw strings uses this helper.
 *   This only swaps the visible wording at render time so users see
 *   "Anniversary" while the underlying data, markers, and matching logic remain
 *   untouched. Case-insensitive, whole-word, preserves the rest of the string.
 */
export const displayPackageLabel = (label: string | null | undefined): string => {
  if (!label) return '';
  return String(label).replace(/Birthday/gi, (m) =>
    m === m.toUpperCase() ? 'ANNIVERSARY' : m[0] === 'B' ? 'Anniversary' : 'anniversary'
  );
};
